import React, { useState, useEffect, useCallback } from "react";
import Form from "react-bootstrap/Form";
import _ from "lodash";
import uuid from "react-uuid";
import Loading from "../../../common/components/loading/loading";
import { getPriceBases } from "../../../common/services/markets/priceBasisService";
import SearchBox from "../../../common/components/search/searchBox";
import PriceBasesTable from "./priceBasesTable";
import { useTranslation } from "react-i18next";

const PriceBases = ({ onClick, allowEdit }) => {
  const { t } = useTranslation(["markets"]);
  const [priceBases, setPriceBases] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [sortColumn, setSortColumn] = useState({ path: "name", order: "asc" });
  const [isLoading, setIsLoading] = useState(true);
  const [paginationState, setPaginationState] = useState({
    currentPage: 0,
    pageSize: 20,
  });

  useEffect(() => {
    const fetchPriceBases = async () => {
      const data = await getPriceBases();
      setPriceBases(data);
      setIsLoading(false);
    };

    fetchPriceBases();
  }, []);

  const handlePriceBasisUpdated = useCallback((priceBasis) => {
    setPriceBases((prevPriceBases) => {
      const updatedPriceBases = [...prevPriceBases];
      const index = updatedPriceBases.findIndex(
        (u) => u.priceBasisId === priceBasis.priceBasisId
      );
      if (index > -1) {
        updatedPriceBases.splice(index, 1);
      }
      updatedPriceBases.push(priceBasis);
      return updatedPriceBases;
    });
  }, []);

  const handleSearch = (query) => {
    setSearchQuery(query);
    setPaginationState((prev) => ({
      ...prev,
      currentPage: 0,
    }));
  };

  const handleSort = (colId, sort) => {
    if (colId && sort) {
      setSortColumn({
        path: colId,
        order: sort,
      });
    }
  };

  const handleToggleActiveOnly = () => {
    setActiveOnly(!activeOnly);
    setPaginationState((prev) => ({
      ...prev,
      currentPage: 0,
    }));
  };

  const handlePriceBasisSelected = (priceBasis, isNew = false) => {
    // Inject the callback function before calling back to the onClick event
    onClick(priceBasis, handlePriceBasisUpdated, isNew);
  };

  const handlePaginationChanged = useCallback((params) => {
    if (params && params.api) {
      setPaginationState({
        currentPage: params.api.paginationGetCurrentPage(),
        pageSize: params.api.paginationGetPageSize(),
      });
    }
  }, []);

  const getFilteredData = () => {
    let filtered = priceBases;

    if (activeOnly) filtered = filtered.filter((u) => u.active);

    if (searchQuery)
      filtered = filtered.filter(
        (u) =>
          (u.name != null &&
            u.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (u.description != null &&
            u.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );

    const sorted = _.orderBy(filtered, [sortColumn.path], [sortColumn.order]);

    return { totalCount: filtered.length, data: sorted };
  };

  if (isLoading) return <Loading />;

  const { totalCount, data: filteredPriceBases } = getFilteredData();

  const newPriceBasis = {
    priceBasisId: uuid(),
    name: t("NewPriceBasis"),
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col">
          <SearchBox value={searchQuery} onChange={handleSearch} />
        </div>
        <div className="col">
          <Form.Switch
            className="switch"
            type="switch"
            label={t("ActiveOnly")}
            checked={activeOnly}
            value={activeOnly}
            onChange={handleToggleActiveOnly}
          />
        </div>
        <div className="col">
          {allowEdit && (
            <button
              onClick={() => handlePriceBasisSelected(newPriceBasis, true)}
              className="btn btn-sm btn-primary float-sm-right"
            >
              {t("AddPriceBasis")}
            </button>
          )}
        </div>
      </div>
      <p>{t("RetrievedPriceBasis", { count: totalCount })}</p>
      <PriceBasesTable
        priceBases={filteredPriceBases}
        onSort={handleSort}
        onRowClick={handlePriceBasisSelected}
        onPaginationChanged={handlePaginationChanged}
        paginationState={paginationState}
      />
    </div>
  );
};

export default PriceBases;
